<template>
    <div>
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <template v-else>
            <v-row dense class="mb-2">
                <v-col cols="12" sm="4">
                    <v-card class="rr-kpi-card quaroshadow" outlined>
                        <div class="rr-kpi-label">Total Mentions</div>
                        <div class="rr-kpi-value">{{ totals.total }}</div>
                        <div class="rr-kpi-sub">As of the end of the period</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-card class="rr-kpi-card quaroshadow" outlined>
                        <div class="rr-kpi-label">New Mentions</div>
                        <div class="rr-kpi-value rr-kpi-value--up">{{ totals.newCount }}</div>
                        <div class="rr-kpi-sub">Found on the end date only</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-card class="rr-kpi-card quaroshadow" outlined>
                        <div class="rr-kpi-label">Lost Mentions</div>
                        <div class="rr-kpi-value rr-kpi-value--down">{{ totals.lostCount }}</div>
                        <div class="rr-kpi-sub">Present on the start date only</div>
                    </v-card>
                </v-col>
            </v-row>
            <div v-if="platformRows.length" class="rr-pmo">
                <div class="rr-pmo-head">
                    <span class="rr-pmo-col rr-pmo-col--platform">Platform</span>
                    <span class="rr-pmo-col">Mentions</span>
                    <span class="rr-pmo-col">New</span>
                    <span class="rr-pmo-col">Lost</span>
                </div>
                <div v-for="row in platformRows" :key="row.key" class="rr-pmo-row">
                    <span class="rr-pmo-col rr-pmo-col--platform">
                        <span v-if="row.logo" class="rr-platform-logo rr-platform-logo--sm" v-html="row.logo"></span>
                        {{ row.label }}
                    </span>
                    <span class="rr-pmo-col">
                        <span class="rr-pmo-count">{{ row.total }}</span>
                        <span class="rr-pmo-bar"><span class="rr-pmo-bar-fill rr-pmo-bar-fill--total" :style="{ width: row.totalPct + '%' }"></span></span>
                    </span>
                    <span class="rr-pmo-col">
                        <span class="rr-pmo-count">{{ row.newCount }}</span>
                        <span class="rr-pmo-bar"><span class="rr-pmo-bar-fill rr-pmo-bar-fill--new" :style="{ width: row.newPct + '%' }"></span></span>
                    </span>
                    <span class="rr-pmo-col">
                        <span class="rr-pmo-count">{{ row.lostCount }}</span>
                        <span class="rr-pmo-bar"><span class="rr-pmo-bar-fill rr-pmo-bar-fill--lost" :style="{ width: row.lostPct + '%' }"></span></span>
                    </span>
                </div>
            </div>
            <div v-else class="rr-empty">No mention data for the selected filters.</div>
        </template>
    </div>
</template>

<script>
    import { fetchMentionsSnapshot, platformDaysInRange } from "../reportApi"
    import { getPlatformMeta } from "../../../utils/platformMeta"

    export default {
        name: "MentionsOverviewSection",
        props: {
            filters: { type: Object, required: true },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] },
            platformKeys: { type: Array, default: () => [] }
        },
        data() {
            return {
                loading: true,
                totals: { total: 0, newCount: 0, lostCount: 0 },
                platformRows: []
            }
        },
        methods: {
            mentionKey(item) {
                const platform = (item?.platform || "").toString().trim().toLowerCase()
                const promptText = (item?.prompt || "").toString().trim().toLowerCase().replace(/\s+/g, " ")
                return `${platform}::${promptText}`
            },
            buildPlatformRows(totalRows, newRows, lostRows) {
                const countByPlatform = (rows) => {
                    const map = new Map()
                    rows.forEach((item) => {
                        const key = (item?.platform || "unknown").toString().trim().toLowerCase() || "unknown"
                        map.set(key, (map.get(key) || 0) + 1)
                    })
                    return map
                }
                const totalMap = countByPlatform(totalRows)
                const newMap = countByPlatform(newRows)
                const lostMap = countByPlatform(lostRows)
                const unionKeys = new Set([...totalMap.keys(), ...newMap.keys(), ...lostMap.keys()])
                const ordered = [...this.platformKeys, ...Array.from(unionKeys).filter((k) => !this.platformKeys.includes(k))]

                const rows = ordered
                    .filter((key) => unionKeys.has(key))
                    .map((key) => {
                        const meta = getPlatformMeta(key)
                        return {
                            key,
                            label: meta.label || key,
                            logo: meta.logo || null,
                            total: totalMap.get(key) || 0,
                            newCount: newMap.get(key) || 0,
                            lostCount: lostMap.get(key) || 0
                        }
                    })
                    .sort((a, b) => b.total - a.total)

                const maxTotal = Math.max(1, ...rows.map((r) => r.total))
                const maxNew = Math.max(1, ...rows.map((r) => r.newCount))
                const maxLost = Math.max(1, ...rows.map((r) => r.lostCount))
                const scale = (v, max) => (v > 0 ? Math.max(5, Math.min(100, (v / max) * 100)) : 0)
                return rows.map((r) => ({ ...r, totalPct: scale(r.total, maxTotal), newPct: scale(r.newCount, maxNew), lostPct: scale(r.lostCount, maxLost) }))
            },
            findPrimaryBrand() {
                const mainName = (this.project?.brand?.brand_name || "").toLowerCase()
                const list = Array.isArray(this.brands) ? this.brands : []
                return (
                    list.find(
                        (b) =>
                            (b.canonicalName || "").toLowerCase() === mainName ||
                            (Array.isArray(b.mentions) && b.mentions.some((m) => (m || "").toLowerCase() === mainName))
                    ) ||
                    list[0] ||
                    null
                )
            },
            async fetchAll() {
                this.loading = true
                try {
                    // the by-brand endpoint expects a brand OBJECT (canonicalName + mentions), not a name string
                    const brandObj = this.findPrimaryBrand()
                    if (!brandObj || !this.platformKeys.length) {
                        this.platformRows = []
                        return
                    }
                    const brand = { canonicalName: brandObj.canonicalName, brand_name: brandObj.brand_name || brandObj.canonicalName, mentions: brandObj.mentions || [] }
                    const scopes = this.platformKeys.map((key) => ({ key, range: platformDaysInRange(this.project, this.filters.selectedDates, key) })).filter((s) => s.range)
                    if (!scopes.length) {
                        this.platformRows = []
                        return
                    }
                    const snapshots = await Promise.all(
                        scopes.map(async ({ key, range }) => {
                            const common = { projectId: this.projectId, brand, platform: key, tags: this.filters.tags, tagsOperator: this.filters.tagsOperator }
                            const [startRows, endRows] = await Promise.all([
                                fetchMentionsSnapshot(this.companyId, { ...common, day: range.start }),
                                fetchMentionsSnapshot(this.companyId, { ...common, day: range.end })
                            ])
                            return { key, startRows, endRows }
                        })
                    )
                    const allCurrent = []
                    const allNew = []
                    const allLost = []
                    snapshots.forEach(({ startRows, endRows }) => {
                        const startMap = new Map()
                        startRows.forEach((item) => {
                            const key = this.mentionKey(item)
                            if (key && !startMap.has(key)) startMap.set(key, item)
                        })
                        const endMap = new Map()
                        endRows.forEach((item) => {
                            const key = this.mentionKey(item)
                            if (key && !endMap.has(key)) endMap.set(key, item)
                        })
                        endMap.forEach((item, key) => {
                            allCurrent.push(item)
                            if (!startMap.has(key)) allNew.push(item)
                        })
                        startMap.forEach((item, key) => {
                            if (!endMap.has(key)) allLost.push(item)
                        })
                    })
                    this.totals = { total: allCurrent.length, newCount: allNew.length, lostCount: allLost.length }
                    this.platformRows = this.buildPlatformRows(allCurrent, allNew, allLost)
                } catch (e) {
                    this.platformRows = []
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>
